// Teams, for test-takers: the join page (/t/:code), the leader link (/t/lead#token) and the
// leader's report (/teams/:teamId).

import { useEffect, useId, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Loading, Message, Page } from "../components/Page";
import { TeamReport, type TeamReportData } from "../components/TeamReport";
import { ApiError, api, type WhoAmI } from "../lib/api";
import { versionFromParam } from "../lib/copy";
import type { MyProfile } from "../lib/types";

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function JoinTeamPage() {
  const { code = "" } = useParams();
  const [params] = useSearchParams();
  // /t/{code}?v=4 sends the team to the start form for that scoring version.
  const scoringVersion = versionFromParam(params.get("v"));
  const consentId = useId();
  const [invite, setInvite] = useState<{ name: string; consent: string; alreadyMember: boolean } | null>(null);
  const [missing, setMissing] = useState(false);
  const [who, setWho] = useState<WhoAmI | null>(null);
  const [profiles, setProfiles] = useState<MyProfile[]>([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.teamInvite(code).then(setInvite).catch(() => setMissing(true));
    api
      .whoAmI()
      .then((w) => {
        setWho(w);
        if (w.signedIn) api.myProfiles().then((d) => setProfiles(d.profiles.filter((p) => p.status === "completed"))).catch(() => undefined);
      })
      .catch(() => setWho({ signedIn: false, email: null, firstName: null }));
  }, [code]);

  if (missing) {
    return (
      <Message title="This team link isn't working">
        <p>Check the whole link, or ask whoever sent it for a new one.</p>
      </Message>
    );
  }
  if (!invite || !who) return <Loading />;

  const share = async (takeId: string) => {
    setError(null);
    if (!consent) return setError("Tick the box to agree first.");
    setBusy(takeId);
    try {
      await api.shareWithTeam(code, takeId);
      setDone(true);
    } catch {
      setError("That didn't work. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Page>
      <p className="eyebrow mt-10 text-accent-text">Team</p>
      <h1 className="heading mt-2 text-[30px] leading-[1.1]">{invite.name}</h1>
      <p className="mt-3 text-[17px] leading-snug">
        Your team is taking the Gifts Profile together. Your results join a team report that shows where the team is strong and where it has gaps.
      </p>

      {done || invite.alreadyMember ? (
        <p role="status" className="mt-6 rounded-[16px] border border-line bg-panel p-4 text-[16px]">
          You're in this team. <Link to="/me">See your profiles</Link>
        </p>
      ) : (
        <>
          <label htmlFor={consentId} className="mt-6 flex items-start gap-3 rounded-[16px] border border-line bg-panel p-4 text-[15px] leading-snug">
            <input id={consentId} type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{invite.consent}</span>
          </label>

          <Link
            to={`/?team=${encodeURIComponent(code)}${consent ? "&agreed=1" : ""}${scoringVersion !== undefined ? `&v=${scoringVersion}` : ""}`}
            className="btn btn-primary mt-5 w-full no-underline"
            aria-disabled={!consent}
            onClick={(e) => {
              if (!consent) {
                e.preventDefault();
                setError("Tick the box to agree first.");
              }
            }}
          >
            Take the profile for this team
          </Link>

          {who.signedIn && profiles.length > 0 ? (
            <section className="mt-8 border-t border-line pt-6" aria-labelledby="share-title">
              <h2 id="share-title" className="heading text-[19px]">
                Or share a profile you've already taken
              </h2>
              <ul className="mt-3 flex flex-col gap-2">
                {profiles.map((p) => (
                  <li key={p.takeId} className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-line bg-panel px-4 py-3">
                    <span>
                      <span className="font-semibold">{p.profileName ?? "Your profile"}</span>
                      <span className="block text-[13px] text-muted">{formatDate(p.completedAt ?? p.startedAt)}</span>
                    </span>
                    <button type="button" className="btn btn-secondary min-h-11 px-4 text-[13px]" onClick={() => void share(p.takeId)} disabled={busy !== null}>
                      {busy === p.takeId ? "Sharing…" : "Share this one"}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : !who.signedIn ? (
            <p className="mt-6 text-[15px] text-muted">
              Already taken it and saved your results? <Link to="/sign-in">Sign in</Link>, then open this link again to share that profile.
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="mt-4 text-[15px] text-danger">
              {error}
            </p>
          ) : null}
        </>
      )}
    </Page>
  );
}

/** /t/lead#token: opening it changes nothing (link previews); Continue makes the signed-in person a leader. */
export function LeadTeamPage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [who, setWho] = useState<WhoAmI | null>(null);
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.hash) history.replaceState(null, "", window.location.pathname);
    api
      .whoAmI()
      .then(setWho)
      .catch(() => setWho({ signedIn: false, email: null, firstName: null }));
  }, []);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      setTeam((await api.leadTeam(token)).team);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "That leader link has expired or was already used. Ask for a new one." : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!who) return <Loading />;
  return (
    <Page>
      <p className="eyebrow mt-10 text-accent-text">Team leader</p>
      <h1 className="heading mt-2 text-[30px] leading-[1.1]">Lead a team</h1>
      {team ? (
        <p role="status" className="mt-5 text-[17px]">
          You now lead {team.name}. <Link to={`/teams/${team.id}`}>Open the team report</Link>
        </p>
      ) : !token ? (
        <p className="mt-4 text-[17px]">This link is incomplete. Open the whole link you were sent.</p>
      ) : !who.signedIn ? (
        <div className="mt-4 text-[17px] leading-snug">
          <p>To lead a team, sign in first. Leaders take the profile too, then save their results with a password.</p>
          <p className="mt-4 flex flex-col gap-2 text-[15px]">
            <Link to="/sign-in">Sign in</Link>
            <Link to="/">Take the profile first</Link>
          </p>
          <p className="mt-4 text-[14px] text-muted">Then open this leader link again. It works once and lasts a week.</p>
        </div>
      ) : (
        <>
          <p className="mt-4 text-[17px]">You'll lead this team as {who.email}. You'll see the team report, with first names.</p>
          <button type="button" className="btn btn-primary mt-6 w-full" onClick={() => void go()} disabled={busy}>
            {busy ? "Working…" : "Continue"}
          </button>
        </>
      )}
      {error ? (
        <p role="alert" className="mt-4 text-[15px] text-danger">
          {error}
        </p>
      ) : null}
    </Page>
  );
}

export function TeamReportPage() {
  const { teamId = "" } = useParams();
  const [data, setData] = useState<{ team: { id: string; name: string }; report: TeamReportData } | null>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.teamReport(teamId).then(setData).catch(() => setMissing(true));
  }, [teamId]);
  if (missing) {
    return (
      <Message title="Team report">
        <p>This report is for the team's leaders. Sign in as a leader to see it.</p>
        <p className="mt-6 text-[15px]">
          <Link to="/sign-in">Sign in</Link>
        </p>
      </Message>
    );
  }
  if (!data) return <Loading label="Loading the team report" />;
  return (
    <Page width="wide" right={<Link to="/me">Your profiles</Link>}>
      <p className="eyebrow mt-8 text-accent-text">Team report</p>
      <h1 className="heading mt-2 mb-6 text-[30px] leading-[1.1]">{data.team.name}</h1>
      <TeamReport r={data.report} />
    </Page>
  );
}
